import type { Cart, LineItem } from '@az/middleware';

export function useCartGetters() {
  function getDiscount(product: LineItem) {
    return product?.price?.discounted;
  }

  function getSpecialPrice(product: LineItem) {
    const discount = getDiscount(product);

    if (isLineItem(product)) {
      const { discountedPricePerQuantity } = product;
      const discountsLength = discountedPricePerQuantity.length;

      if (discountsLength > 0) {
        const discountedPrice = discountedPricePerQuantity[discountsLength - 1].discountedPrice;

        return getPrice(discountedPrice);
      }
    }

    if (discount?.discount.isActive) {
      return getPrice(discount);
    }

    return null;
  }

  function getPrice(
    price:
      | LineItem['price']
      | LineItem['price']['discounted']
      | LineItem['discountedPricePerQuantity'][number]['discountedPrice'],
  ) {
    return price?.value?.centAmount ? price.value.centAmount / 100 : null;
  }

  function getItemQty(product: LineItem): number {
    return product?.quantity ?? 0;
  }

  function isLineItem(product: LineItem) {
    return product.__typename === 'LineItem';
  }

  function getItemPrice(product: LineItem) {
    if (!product) {
      return { regular: null, special: null };
    }

    const quantity = isLineItem(product) ? getItemQty(product) : 1;
    const regularPriceSingleItem = getPrice(product.price);
    const regularPrice = regularPriceSingleItem ? regularPriceSingleItem * quantity : regularPriceSingleItem;
    const specialPriceSingleItem = getSpecialPrice(product);
    const specialPrice = specialPriceSingleItem ? specialPriceSingleItem * quantity : specialPriceSingleItem;

    return {
      regular: regularPrice,
      special: specialPrice,
    };
  }

  function getCartSubtotalPrice(cart: Cart, selectSpecialPrices = false) {
    return (
      cart?.lineItems.reduce((total, cartItem) => {
        const { special, regular } = getItemPrice(cartItem);
        const itemPrice = (selectSpecialPrices && special) || regular;
        return total + itemPrice;
      }, 0) ?? 0
    );
  }

  function getTotals(cart: Cart) {
    if (!cart) {
      return {
        total: 0,
        subtotal: 0,
        special: 0,
      };
    }

    return {
      total: cart.totalPrice.centAmount / 100,
      subtotal: getCartSubtotalPrice(cart),
      special: getCartSubtotalPrice(cart, true),
    };
  }

  function getItemSku(product: LineItem): string {
    return product?.variant?.sku ?? '';
  }

  return {
    getTotals,
    getItemPrice,
    getItemQty,
    getItemSku,
  };
}
